import * as React from 'react'

import styled from '@emotion/styled'

import LoadingIcon from '../LoadingIcon/LoadingIcon'

const LoadingPlaceholder = styled.div<{
  imageLoaded: boolean
  placeholderHeight: string
  placeholderWidth?: string
}>`
  align-items: center;
  background: ${props => props.theme.colors.behindCardBackground};
  border-radius: 1rem;
  display: ${props => (props.imageLoaded ? 'none' : 'flex')};
  height: ${props => props.placeholderHeight};
  width: ${props => props.placeholderWidth || '100%'};
  justify-content: center;
`

const Image = styled.img<{ imageLoaded: boolean }>`
  display: ${props => (props.imageLoaded ? 'block' : 'none')};
`

type LoadingImageProps = {
  alt: string
  placeholderHeight: string
  placeholderWidth?: string
} & React.ImgHTMLAttributes<HTMLImageElement>

/**
 * This displays a skeleton loading placeholder while the image is loading.
 * Since we don't know the image's rendered height until it has loaded,
 * use the placeholderHeight to fill the image's space.
 *
 * Example of how to use:
 * <LoadingImage
 *  alt={t('Example alt', { context: 'alt text for image' })}
 *  placeholderHeight="12.5rem"
 *  src={imageSrc}
 * />
 */
const LoadingImage = ({ alt, placeholderHeight, placeholderWidth, ...props }: LoadingImageProps) => {
  const [imageLoaded, setImageLoaded] = React.useState(false)

  return (
    <>
      <LoadingPlaceholder
        imageLoaded={imageLoaded}
        placeholderHeight={placeholderHeight}
        placeholderWidth={placeholderWidth}
        data-testid="loading-placeholder"
      >
        <LoadingIcon color="secondaryIcon" size={40} />
      </LoadingPlaceholder>
      <Image {...props} alt={alt} imageLoaded={imageLoaded} onLoad={() => setImageLoaded(true)} />
    </>
  )
}

export default LoadingImage
